import LS from './LS';

type TokenGetter = () => Promise<string>;

let auth0TokenGetter: TokenGetter | null = null;
let lastAuth0Token = '';

/**
 * Registers the Auth0 SDK's token getter for use outside React.
 *
 * The Apollo client and the @pi/api-client axios instance are both module-level singletons created
 * before any component mounts, so they cannot call `useAuth0()`. A component inside `Auth0Provider`
 * hands `getAccessTokenSilently` in here instead.
 *
 * @param getter - pass null on unmount to fall back to the native token
 */
export const registerAuth0TokenGetter = (getter: TokenGetter | null): void => {
    auth0TokenGetter = getter;
};

export const isAuth0Session = (): boolean => LS.get('authProvider') === 'auth0';

/**
 * Resolves the credential to send as the `jwt` header.
 *
 * For Auth0 sessions this asks the SDK every time, which is what keeps a rotating access token from
 * going stale. For native sessions it reads the long-lived PI token out of local storage, exactly as
 * before.
 */
export const getApiToken = async (): Promise<string> => {
    if (isAuth0Session() && auth0TokenGetter) {
        try {
            lastAuth0Token = await auth0TokenGetter();
            return lastAuth0Token;
        } catch {
            // Refresh failed (expired or revoked refresh token). Fall through to an empty
            // credential so the request comes back unauthenticated and the app shows the login
            // screen, rather than hanging or throwing inside an Apollo link.
            return '';
        }
    }

    return LS.get('jwt') || '';
};

/**
 * Best-effort token for callers that cannot await - currently only building a download href.
 *
 * In Auth0 mode this returns the most recently resolved access token rather than asking the SDK,
 * since there is nowhere to put an await. Access tokens live for hours, and any request the app has
 * made recently will have refreshed this, so it is effectively current.
 */
export const getApiTokenSync = (): string =>
    isAuth0Session() ? lastAuth0Token : LS.get('jwt') || '';
